/**
 * Pure helpers for resolving a dashboard param's options and default value.
 *
 * Deliberately light (no warehouse driver, no sql-allowlist; its one import, `time-presets`, is itself
 * aliased into the SPA) so ONE implementation serves every surface: the server imports it directly, and the SPA imports it via the
 * `@server/dashboard-param-defaults` alias. It lives here rather than in `dashboard-param-options.ts`
 * because that module pulls in `sql-allowlist` (node-sql-parser), which must not bundle into the SPA.
 *
 * Keeping one copy matters: these rules must agree across the app, the public view, and the MCP
 * surfaces — a fix applied to one copy and not the others silently reintroduces the phantom-default
 * bug on the un-updated surface. See AI-1186.
 *
 * The standalone MCP-app bundles (`src/mcp-external/apps/*`) still re-implement these inline — AGENTS.md
 * sanctions that for their bundle-size budget.
 */
import { resolveAdjustableTimeRangeDefault } from './time-presets.js';

/** Drop options whose value is null/undefined/empty — a blank option can't be selected and is useless as a filter. */
export function filterValidParamOptions<T extends { value: string | null | undefined }>(
  options: readonly T[] | undefined,
): T[] {
  if (!options) return [];
  return options.filter((opt) => opt.value != null && opt.value !== '');
}

/**
 * Resolve a select param's value: the author `default` only when it's a real option, else the first
 * option — `undefined` when there are none (never `''`, which isn't a selectable option). Keeps a
 * phantom `default` (one not in the loaded/static options) from binding into the panel query.
 */
export function resolveSelectDefault(
  paramDefault: string | string[] | undefined,
  options: readonly { value: string }[],
): string | undefined {
  if (typeof paramDefault === 'string' && options.some((o) => o.value === paramDefault)) return paramDefault;
  return options[0]?.value;
}

/**
 * The parameter fields `computeDefaults` reads. Structural, so both the server's `DashboardParam` and
 * the SPA's (which adds `hasDynamicOptions`) pass without a type import.
 */
export type DefaultableDashboardParam = {
  name: string;
  type: 'time_range' | 'select' | 'multi_select';
  default?: string | string[];
  options?: readonly { value: string }[];
  sql?: string;
  hasDynamicOptions?: boolean;
  fixed_preset?: string;
};

/** True when options are fetched at runtime (SQL or public `hasDynamicOptions` flag). */
function isDynamicParam(param: DefaultableDashboardParam): boolean {
  return !!param.sql || param.hasDynamicOptions === true;
}

/**
 * Default param values from a dashboard spec's parameters: the values a dashboard uses on first
 * load, before any dynamic options arrive. Shared by the SPA's first load and the ACT2-1286 repair
 * probe, so a probe binds exactly what a first load binds. A dynamic select's options query never
 * runs here; a select left with no value is omitted (it binds NULL). `now` makes tests deterministic.
 */
export function computeDefaults(
  parameters: readonly DefaultableDashboardParam[],
  now: Date = new Date(),
): Record<string, string | string[]> {
  const defaults: Record<string, string | string[]> = {};
  for (const param of parameters) {
    if (param.type === 'time_range') {
      // fixed_preset params are resolved server-side; exclude from URL state.
      if (param.fixed_preset) continue;
      const { start, end } = resolveAdjustableTimeRangeDefault(param.default, now);
      defaults[`${param.name}_start`] = start;
      defaults[`${param.name}_end`] = end;
    } else if (param.type === 'select') {
      const opts = filterValidParamOptions(param.options);
      if (isDynamicParam(param)) {
        // Options load later; seed the raw default (or first fallback), then the deferred patch corrects it.
        if (param.default !== undefined) defaults[param.name] = param.default;
        else if (opts[0]) defaults[param.name] = opts[0].value;
      } else {
        // Static: resolve against known options so an out-of-options default can't reach (and throw in) the query.
        const fallback = resolveSelectDefault(param.default, opts);
        if (fallback !== undefined) defaults[param.name] = fallback;
      }
    } else {
      // multi_select: author default if present, else empty. A dynamic multi_select with no selection
      // is filled to all options once they load (buildDeferredParamPatch); a static one starts empty.
      defaults[param.name] = param.default ?? [];
    }
  }
  return defaults;
}
